// Módulos
import {
  Component,
  Input,
  Output,
  OnInit,
  EventEmitter,
  OnChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Rxjs
import { Subscription } from 'rxjs';

// Interfaces
import { IColumns } from '@shared-module/interfaces/table';

@Component({
    selector: 'sbi-cot-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    host: {
        '(document:click)': 'onClick($event)',
    },
    standalone: false
})
export class TableComponent implements OnInit, OnChanges {
  // Inputs
  @Input() dataSource: any = [];
  @Input() columns: IColumns[];
  @Input() hasViewAction: boolean = false;
  @Input() hasEditAction: boolean = false;
  @Input() hasDeleteAction: boolean = false;
  @Input() editIconClass: string = 'fas fa-pen';
  @Input() editActiveIconClass: string = 'fas fa-save';
  @Input() deleteIconClass: string = 'fas fa-trash';
  @Input() viewIconClass: string = 'fas fa-download';
  @Input() sortAscIconClass: string = 'fas fa-arrow-up';
  @Input() sortDescIconClass: string = 'fas fa-arrow-down';
  @Input() filterSearchIconClass: string = 'fa fa-search';
  @Input() filterClearIconClass: string = 'fa fa-times';

  // Outputs
  @Output() viewActionEmiter = new EventEmitter();
  @Output() deleteActionEmiter = new EventEmitter();
  @Output() editActionEmiter = new EventEmitter();
  @Output() dropdownItemChangeEmiter = new EventEmitter();
  @Output() onSelectEmiter = new EventEmitter();
  @Output() onDeSelectEmiter = new EventEmitter();
  @Output() onSelectAllEmiter = new EventEmitter();
  @Output() onDeSelectAllEmiter = new EventEmitter();

  // Public variables
  public filteredDataSource: any;
  public dropdownDataForm: FormGroup;

  // Subscriptions
  private _subscriptions$: Subscription[] = [];

  // Constructor
  constructor(private _formBuilder: FormBuilder) {}

  // On component init
  ngOnInit() {
    this.filteredDataSource = this.dataSource;

    // Create dropdown form
    this.dropdownDataForm = this._formBuilder.group({});
    if (this.dataSource) this._buildDropdownDataForm();
  }

  ngOnChanges(changes) {
    if (changes.dataSource) {
      this.dataSource = changes.dataSource.currentValue;
      this.filteredDataSource = this.dataSource;
      this._filterTableBy();
      if (this.dataSource) this._buildDropdownDataForm();
    }
  }

  // Create form to watch the dropdown item changes
  private _buildDropdownDataForm(): void {
    const formItemsDropdown = this._setFormItemsDropdown();
    const formItemsDate = this._setFormItemsDate();
    const formItemsInput = this._setFormItemsInput();
    const formItems = {
      ...formItemsDropdown,
      ...formItemsDate,
      ...formItemsInput,
    };
    this.dropdownDataForm = this._formBuilder.group(formItems);

    // Watcher
    this._dropdownSelectedWatcher();
    this._dateFromSelectedWatcher();
    this._dateToSelectedWatcher();
  }

  // Private method to add the dropdown data in the form
  private _setFormItemsDropdown(): any {
    let formItems = {};
    // set form control dropdown data
    this.columns.map(column => {
      if (column.isDropdown)
        formItems = this._setFormItemsDropdownSimple(column);
      else if (column.isMultiselectDropdown)
        formItems = this._setFormItemsDropdownMultiple(column);
    });

    return formItems;
  }

  // Private method to add simple dropdown data in the form
  private _setFormItemsDropdownSimple(column: IColumns): any {
    const formItems = {};

    this.filteredDataSource.map((data, index) => {
      return (formItems[column.name + '_' + index] = [
        { name: data[column.name], value: data[column.name] },
        Validators.required,
      ]);
    });

    return formItems;
  }

  // Private method to add simple dropdown data in the form
  private _setFormItemsDropdownMultiple(column: IColumns): any {
    const formItems = {};

    this.filteredDataSource.map((data, index) => {
      // data can be either an array or a single object
      return (formItems[column.name + '_' + index] = [
        data[column.name],
        Validators.required,
      ]);
    });

    return formItems;
  }

  // Private method to add the input data in the form
  private _setFormItemsInput(): any {
    const formItems = {};
    // set form control input data
    this.columns.map(column => {
      if (column.isEditable) {
        this.filteredDataSource.map((row, index) => {
          if (row.isEditable) {
            return (formItems['input_' + column.name + '_' + index] = [
              row[column.name],
              column.editableValidators,
            ]);
          }
        });
      }
    });

    return formItems;
  }

  // Private method to add the dates data in the form
  private _setFormItemsDate(): any {
    const formItems = {};
    // set form control date from and to
    if (this.dropdownDataForm && this.dropdownDataForm.controls) {
      formItems['dateFrom'] = [
        this.dropdownDataForm.controls['dateFrom']
          ? this.dropdownDataForm.controls['dateFrom'].value
          : '',
      ];
      formItems['dateTo'] = [
        this.dropdownDataForm.controls['dateTo']
          ? this.dropdownDataForm.controls['dateTo'].value
          : '',
      ];
    } else {
      formItems['dateFrom'] = [''];
      formItems['dateTo'] = [''];
    }

    return formItems;
  }

  // Observe the dropdowns changes
  private _dropdownSelectedWatcher(): void {
    this._updateDropdownValues();
  }

  // Private function to update the dropdown values
  private _updateDropdownValues(): void {
    this.columns.map(column => {
      if (column.isDropdown) {
        this.filteredDataSource.map((data, index) => {
          this._subscriptions$.push(
            this.dropdownDataForm.controls[
              column.name + '_' + index
            ].valueChanges.subscribe(selectedItem => {
              data[column.name] = selectedItem.name;
              this.dataSource[index][column.name] = selectedItem.name;
              this.dropdownItemChangeEmiterCallback();
            })
          );
        });
      }
    });
  }

  // Public function to order table
  public sortTableBy(column: IColumns): void {
    if (column.isDate) this._sortTableByDate(column);
    else if (column.isNumber) this._sortTableByNumber(column);
    else this._sortTableByString(column);
    column.isOrderByAscendant = !column.isOrderByAscendant;

    // Create dropdown form
    if (this.dataSource) this._buildDropdownDataForm();
  }

  // Private function to sort table by date
  private _sortTableByDate(column: IColumns): void {
    if (column.isOrderByAscendant) {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) =>
          (new Date(a[column.name]) > new Date(b[column.name]) && 1) || -1
      );
    } else {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) =>
          (new Date(a[column.name]) < new Date(b[column.name]) && 1) || -1
      );
    }
  }

  // Private function to sort table by number
  private _sortTableByNumber(column: IColumns): void {
    if (column.isOrderByAscendant) {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) => a[column.name] - b[column.name]
      );
    } else {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) => b[column.name] - a[column.name]
      );
    }
  }

  // Private function to sort table by string
  private _sortTableByString(column: IColumns): void {
    if (column.isOrderByAscendant) {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) =>
          (a[column.name].toLowerCase() > b[column.name].toLowerCase() && 1) ||
          -1
      );
    } else {
      this.filteredDataSource = this.filteredDataSource.sort(
        (a, b) =>
          (a[column.name].toLowerCase() < b[column.name].toLowerCase() && 1) ||
          -1
      );
    }
  }

  // Public function to set the column filter
  public setColumnFilter(event, column: IColumns): void {
    column.filterString = event.target.value;
    this._filterTableBy();
  }

  // Private function to filter table
  private _filterTableBy(): void {
    this.filteredDataSource = this.dataSource;
    if (
      !!this.columns.find(col => !!col.filterString) ||
      !!this.columns.find(col => !!col.filterDateFrom) ||
      !!this.columns.find(col => !!col.filterDateTo)
    ) {
      this._filterTableByString();
      this._filterTableByDate();
    }
  }

  // Private method to filter the grid by string
  private _filterTableByString(): void {
    if (this.columns.find(col => !!col.filterString)) {
      this.columns.map(col => {
        if (col.filterString) {
          this.filteredDataSource = this.filteredDataSource.filter(data => {
            if (
              String(data[col.name])
                .toLowerCase()
                .indexOf(col.filterString.toLowerCase()) >= 0
            )
              return data;
          });
        } else {
          col.isFilterEnabled = false;
        }
      });
    }
  }

  // Privame method to filter the grid by date
  private _filterTableByDate(): void {
    if (
      !!this.columns.find(col => !!col.filterDateFrom) ||
      !!this.columns.find(col => !!col.filterDateTo)
    ) {
      const column = this.columns.find(
        col => !!col.filterDateFrom || !!col.filterDateTo
      );
      const dateFrom = new Date(column.filterDateFrom).setHours(0, 0, 0, 0);
      const dateTo = new Date(column.filterDateTo).setHours(0, 0, 0, 0);

      this.filteredDataSource = this.filteredDataSource.filter(data => {
        const dateColumn = new Date(data[column.name]).setHours(0, 0, 0, 0);
        if (!column.filterDateTo && !column.filterDateFrom) return data;
        else if (!!column.filterDateTo && !!column.filterDateFrom) {
          if (dateColumn >= dateFrom && dateColumn <= dateTo) return data;
        } else if (!!column.filterDateFrom && dateColumn >= dateFrom)
          return data;
        else if (!!column.filterDateTo && dateColumn <= dateTo) return data;
      });
    }
  }

  // Public method to set if the filter is enabled or disabled
  public setFilterEnabledStatus(column: IColumns): void {
    if (column.isDate) column.isDateMenuOpened = !column.isDateMenuOpened;
    else column.isFilterEnabled = !column.isFilterEnabled;

    if (!column.isFilterEnabled) column.filterString = '';

    if (column.isDate && !column.isDateMenuOpened) {
      column.filterDateFrom = '';
      column.filterDateTo = '';
      this.dropdownDataForm.controls['dateFrom'].setValue('');
      this.dropdownDataForm.controls['dateTo'].setValue('');
    }

    this._buildDropdownDataForm();
    this._filterTableBy();
  }

  // Private method to watch the date from changes
  private _dateFromSelectedWatcher(): void {
    this._subscriptions$.push(
      this.dropdownDataForm.controls['dateFrom'].valueChanges.subscribe(
        selectedItem => {
          this.columns.find(column => !!column.isDate).filterDateFrom =
            selectedItem;
          this._filterTableBy();
        }
      )
    );
  }

  // Private method to watch the date from changes
  private _dateToSelectedWatcher(): void {
    this._subscriptions$.push(
      this.dropdownDataForm.controls['dateTo'].valueChanges.subscribe(
        selectedItem => {
          this.columns.find(column => !!column.isDate).filterDateTo =
            selectedItem;
          this._filterTableBy();
        }
      )
    );
  }

  // Public function to call the parent delete function
  public deleteActionEmiterCallback(index, data): void {
    this.deleteActionEmiter.emit({ index: index, data: data });
  }

  // Public function to call the parent view function
  public viewActionEmiterCallback(index, data): void {
    this.viewActionEmiter.emit({ index: index, data: data });
  }

  // Public function to call the parent edit function
  public editActionEmiterCallback(index, data): void {
    if (!this.dropdownDataForm.invalid) {
      this.editActionEmiter.emit({ index: index, data: data });
    }
  }

  // Public function to call the parent edit function with the result
  public editInputValue(column, data, event): void {
    data[column.name] = event.target.value;
  }

  public dropdownItemChangeEmiterCallback(): void {
    this.dropdownItemChangeEmiter.emit(this.dataSource);
  }

  // Public method to handle the clicks out of the component
  public onClick(event): void {
    this._handleDateFilterClick(event);
  }

  // Private method to handle the date filter openned status
  private _handleDateFilterClick(event): void {
    const html = event.target as HTMLElement;
    const dateColumn = this.columns.find(column => !!column.isDate);
    if (dateColumn) {
      dateColumn.isDateMenuOpened =
        html.classList.contains('date-filter-button') ||
        html.closest('.date-menu-container') ||
        html.closest('.mat-calendar')
          ? dateColumn.isDateMenuOpened
          : false;
    }
  }

  public onSelect(item, index): void {
    this.onSelectEmiter.emit({ item, index });
  }

  public onDeSelect(item, index): void {
    this.onDeSelectEmiter.emit({ item, index });
  }

  public onSelectAll(items, index): void {
    this.onSelectAllEmiter.emit({ items, index });
  }

  public onDeSelectAll(items, index): void {
    this.onDeSelectAllEmiter.emit({ items, index });
  }

  public editTable(data): boolean {
    let flag = false;
    this.columns.find(column => {
      if (
        column.isEditable &&
        !column.isDropdown &&
        !column.isMultiselectDropdown &&
        data.isEditable
      ) {
        flag = true;
      }
    });
    return flag;
  }
}
